import { apiClient } from './api';

// API Response wrapper interface - matches backend ApiResponse<T>
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message: string;
  errors?: string[];
}

// Email service interfaces - aligned with backend DTOs
export interface EmailSettingsDto {
  fromEmail: string;
  fromName: string;
  supportEmail: string;
  bccEmail?: string;
  isEnabled: boolean;
  baseUrl: string;
  maxAttachmentSize: number; // bytes, not MB
  allowedAttachmentExtensions: string;
  templateDirectory: string;
  defaultTicketTemplate: string;
  defaultReplyTemplate: string;
  inbound: InboundEmailSettingsDto;
  threading: EmailThreadingSettingsDto;
  spamDetection: EmailSpamSettingsDto;
  domainRestrictions: EmailDomainSettingsDto;
  smtpSettings: SmtpSettingsDto;
}

export interface SmtpSettingsDto {
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string;
  enableSsl: boolean;
}

export interface InboundEmailSettingsDto {
  isEnabled: boolean;
  imapHost: string;
  imapPort: number;
  imapUsername: string;
  imapEnableSsl: boolean;
  inboxFolder: string;
  processedFolder: string;
  spamFolder: string;
  pollIntervalSeconds: number;
  batchSize: number;
  autoCreateCustomers: boolean;
  webhookUrl?: string;
}

export interface EmailThreadingSettingsDto {
  isEnabled: boolean;
  method: ThreadingMethodDto;
  replyPrefixes: string[];
  maxThreadAgeDays: number;
  includeTicketNumber: boolean;
  ticketNumberFormat: string;
}

export interface EmailSpamSettingsDto {
  isEnabled: boolean;
  spamThreshold: number;
  quarantineSpam: boolean;
  spamKeywords: string;
  trustedSenders: string[];
  blockedSenders: string[];
  maxEmailsPerSenderPerHour: number;
  scanAttachments: boolean;
}

export interface EmailDomainSettingsDto {
  isEnabled: boolean;
  allowedDomains: string[];
  restrictionAction: DomainRestrictionActionDto;
  whitelistedEmails: string[];
  logAllAttempts: boolean;
  testingRedirectEmail?: string;
}

export enum ThreadingMethodDto {
  MessageId = 0,
  Subject = 1,
  Both = 2
}

export enum DomainRestrictionActionDto {
  Block = 0,
  LogOnly = 1,
  Redirect = 2
}

export interface EmailTestRequestDto {
  testEmailAddress: string;
}

export interface EmailTestResultDto {
  isSuccess: boolean;
  errors: string[];
  warnings: string[];
  testDate: string;
  testDuration: string;
}

export interface EmailTemplateDto {
  name: string;
  displayName: string;
  description?: string;
  category: string;
  subject: string;
  htmlTemplate: string;
  plainTemplate: string;
  isSystemTemplate: boolean;
  isActive: boolean;
  lastModified: string;
  modifiedBy?: string;
  version: number;
}

export interface EmailTemplateSummaryDto {
  name: string;
  displayName: string;
  category: string;
  isSystemTemplate: boolean;
  isActive: boolean | null;
  lastModified: string | null;
  version: number | null;
}

export interface EmailTemplatePreviewRequestDto {
  templateData: Record<string, any>;
}

export interface EmailPreviewDto {
  subject: string;
  htmlContent: string;
  textContent: string;
}

export interface UpdateEmailTemplateDto {
  displayName: string;
  description?: string;
  category: string;
  subject: string;
  htmlTemplate: string;
  plainTemplate: string;
  isActive: boolean;
}

export interface CustomEmailNotificationDto {
  recipients: string[];
  templateName: string;
  templateData: Record<string, any>;
  customSubject?: string;
}

export interface EmailNotificationHistoryDto {
  id: string;
  ticketId: string;
  ticketNumber: string;
  notificationType: string;
  recipient: string;
  subject: string;
  templateName: string;
  deliveryStatus: EmailDeliveryStatus;
  sentAt: string;
  deliveredAt?: string;
  retryCount: number;
  triggeredBy: string;
  triggeredById: string;
  // BugHerd #163: these are returned by GET /email/notifications/history but were missing
  // from the interface, so the page invented names of its own for them.
  conversationId?: string;
  errorMessage?: string | null;
  providerMessageId?: string | null;
  openCount?: number;
  clickCount?: number;
  priority?: string;
}

export enum EmailDeliveryStatus {
  Pending = 0,
  Sent = 1,
  Delivered = 2,
  Failed = 3,
  Bounced = 4
}

/**
 * Human-readable label for the integer EmailDeliveryStatus the API returns.
 *
 * BugHerd #163: the notification pages switched on lowercase strings ("sent", "delivered"),
 * which an integer can never match, so every row fell through to the default branch.
 */
export const deliveryStatusLabel = (status: EmailDeliveryStatus | number | null | undefined): string => {
  switch (status) {
    case EmailDeliveryStatus.Pending: return 'Pending';
    case EmailDeliveryStatus.Sent: return 'Sent';
    case EmailDeliveryStatus.Delivered: return 'Delivered';
    case EmailDeliveryStatus.Failed: return 'Failed';
    case EmailDeliveryStatus.Bounced: return 'Bounced';
    default: return 'Unknown';
  }
};

export interface EmailNotificationLogDto {
  id: string;
  timestamp: string;
  logLevel: string;
  message: string;
  notificationId: string;
  ticketId: string;
  emailAddress: string;
}

export interface EmailNotificationStatsDto {
  totalSentToday: number;
  totalSentThisWeek: number;
  totalSentThisMonth: number;
  deliveryRate: number;
  failedToday: number;
  pendingDelivery: number;
  averageDeliveryTimeMinutes: number;
  topTemplates: TemplateUsageStatsDto[];
  hourlyStats: HourlyDeliveryStatsDto[];
}

export interface TemplateUsageStatsDto {
  templateName: string;
  usageCount: number;
  successRate: number;
}

export interface HourlyDeliveryStatsDto {
  hour: number;
  date: string;
  emailsSent: number;
  successfulDeliveries: number;
  failedDeliveries: number;
}

export interface EmailThreadDto {
  id: string;
  ticketId: string;
  ticketNumber: string;
  subject: string;
  customerEmail: string;
  rootMessageId: string;
  latestMessageId?: string;
  messageIds: string[];
  createdAt: string;
  lastActivityAt: string;
  messageCount: number;
  status: EmailThreadStatus;
  messages: EmailMessageDto[];
}

export interface EmailMessageDto {
  id: string;
  messageId: string;
  emailThreadId: string;
  conversationId?: string;
  direction: EmailDirection;
  fromEmail: string;
  fromName: string;
  toEmails: string[];
  ccEmails: string[];
  subject: string;
  htmlBody?: string;
  textBody?: string;
  receivedAt: string;
  isProcessed: boolean;
  processingError?: string;
  attachments: EmailMessageAttachmentDto[];
}

export interface EmailMessageAttachmentDto {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId?: string;
  downloadUrl?: string;
}

export enum EmailThreadStatus {
  Active = 0,
  Closed = 1,
  Archived = 2
}

export enum EmailDirection {
  Inbound = 0,
  Outbound = 1
}

export interface EmailAttachmentDto {
  fileName: string;
  contentType: string;
  size: number;
  content: string; // base64 encoded
  isInline: boolean;
  contentId?: string;
}

export interface OutboundEmailRequestDto {
  ticketId: string;
  conversationId?: string;
  recipientEmail: string;
  subject: string;
  body: string;
  isHtml: boolean;
  attachments: EmailAttachmentDto[];
  ccRecipients: string[];
  bccRecipients: string[];
  priority: EmailPriority;
  sendImmediately: boolean;
  scheduledSendTime?: string;
  customHeaders: Record<string, string>;
}

export interface OutboundEmailResultDto {
  isSuccess: boolean;
  messageId?: string;
  errorMessage?: string;
  conversationId?: string;
  emailThreadId?: string;
  providerData: Record<string, string>;
  sentAt: string;
  trackingUrl?: string;
}

export enum EmailPriority {
  Low = 0,
  Normal = 1,
  High = 2,
  Urgent = 3
}

// Additional DTOs aligned with backend
export interface EmailProcessingResultDto {
  isSuccess: boolean;
  ticketId?: string;
  ticketNumber?: string;
  conversationId?: string;
  emailThreadId?: string;
  isSpam: boolean;
  spamScore?: number;
  errorMessage?: string;
  warnings: string[];
  metadata: Record<string, string>;
  attachmentsProcessed: number;
  attachmentsTotalSize: number;
  processingTimeMs: number;
  customerInfo?: EmailCustomerInfoDto;
}

export interface EmailCustomerInfoDto {
  id: string;
  email: string;
  name: string;
  isNewCustomer: boolean;
  companyId?: string;
  companyName?: string;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, any>;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Email service implementation
export const emailService = {
  // Configuration Management (Admin Only)
  getSettings: async () => {
    const response = await apiClient.get<EmailSettingsDto>('/email/settings');
    return response.data;
  },

  updateSettings: async (settings: Partial<EmailSettingsDto>) => {
    const response = await apiClient.put<EmailSettingsDto>('/email/settings', settings);
    return response.data;
  },

  testSystem: async (testData: EmailTestRequestDto) => {
    const response = await apiClient.post<EmailTestResultDto>('/email/test', testData);
    return response.data;
  },

  // Template Management (Admin/Manager)
  getTemplates: async (): Promise<ApiResponse<EmailTemplateSummaryDto[]>> => {
    return await apiClient.get<EmailTemplateSummaryDto[]>('/email/templates');
  },

  getTemplate: async (name: string) => {
    const response = await apiClient.get<EmailTemplateDto>(`/email/templates/${encodeURIComponent(name)}`);
    return response.data;
  },

  previewTemplate: async (name: string, data: Record<string, any>) => {
    const response = await apiClient.post<{ subject: string; htmlContent: string; textContent: string }>(
      `/email/templates/${encodeURIComponent(name)}/preview`,
      data
    );
    return response.data;
  },

  updateTemplate: async (name: string, template: Partial<EmailTemplateDto>) => {
    const response = await apiClient.put<EmailTemplateDto>(
      `/email/templates/${encodeURIComponent(name)}`,
      template
    );
    return response.data;
  },

  // Notification Operations (Admin/Agent)
  sendCustomEmail: async (notification: CustomEmailRequestDto) => {
    const response = await apiClient.post<{ success: boolean; messageId: string; details?: string }>(
      '/email/notifications/send-custom',
      notification
    );
    return response.data;
  },

  getNotificationHistory: async (params?: PaginationParams) => {
    const response = await apiClient.get<PaginatedResponse<EmailNotificationHistoryDto>>(
      '/email/notifications/history',
      { params }
    );
    return response.data;
  },

  getNotificationStats: async () => {
    const response = await apiClient.get<EmailNotificationStatsDto>('/email/notifications/stats');
    return response.data;
  },

  // Processing Operations
  getEmailThreads: async (ticketId: string) => {
    const response = await apiClient.get<EmailThreadDto[]>(`/email/threads/${encodeURIComponent(ticketId)}`);
    return response.data;
  },

  sendOutboundEmail: async (email: OutboundEmailRequestDto) => {
    const formData = new FormData();
    
    // Add basic fields
    formData.append('ticketId', email.ticketId);
    formData.append('subject', email.subject);
    formData.append('htmlContent', email.htmlContent);
    formData.append('priority', email.priority);
    formData.append('trackOpens', email.trackOpens.toString());
    formData.append('trackClicks', email.trackClicks.toString());

    // Add arrays
    email.recipientEmails.forEach(email => formData.append('recipientEmails', email));
    email.ccEmails?.forEach(email => formData.append('ccEmails', email));
    email.bccEmails?.forEach(email => formData.append('bccEmails', email));

    // Add optional fields
    if (email.textContent) formData.append('textContent', email.textContent);
    if (email.templateName) formData.append('templateName', email.templateName);
    if (email.templateData) formData.append('templateData', JSON.stringify(email.templateData));

    // Add attachments
    email.attachments?.forEach((file, index) => {
      formData.append(`attachments`, file);
    });

    const response = await apiClient.post<{ success: boolean; messageId: string; threadId: string }>(
      '/email/outbound',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Utility methods
  validateEmailAddress: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  formatEmailList: (emails: string[]): string => {
    return emails.filter(email => emailService.validateEmailAddress(email)).join(', ');
  },

  getTemplateVariables: (templateType: EmailTemplateDto['templateType']): string[] => {
    const commonVariables = ['{{userFirstName}}', '{{userLastName}}', '{{userEmail}}', '{{companyName}}'];
    
    switch (templateType) {
      case 'ticket_created':
      case 'ticket_updated':
      case 'ticket_closed':
        return [...commonVariables, '{{ticketNumber}}', '{{ticketTitle}}', '{{ticketStatus}}', '{{assignedAgent}}', '{{ticketUrl}}'];
      case 'user_welcome':
        return [...commonVariables, '{{loginUrl}}', '{{temporaryPassword}}'];
      case 'password_reset':
        return [...commonVariables, '{{resetUrl}}', '{{resetToken}}', '{{expiryTime}}'];
      default:
        return commonVariables;
    }
  }
};
